import type { ApiEnvelope } from '@cottage/shared';

const configuredApiUrl: unknown = import.meta.env.VITE_API_URL;
const localApiUrl = 'http://localhost:8787';
// Production admin requests must stay on the Access-protected custom domain.
// Cloudflare routes /api/* to the Worker after attaching the Access session.
const API_URL = import.meta.env.PROD
  ? ''
  : typeof configuredApiUrl === 'string'
    ? configuredApiUrl
    : localApiUrl;
const LOCAL_DEVELOPMENT = import.meta.env.DEV;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function setLocalAdminToken(value: string): void {
  if (LOCAL_DEVELOPMENT) sessionStorage.setItem('cottage-admin-token', value);
}

export function getLocalAdminToken(): string {
  return LOCAL_DEVELOPMENT ? (sessionStorage.getItem('cottage-admin-token') ?? '') : '';
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getLocalAdminToken();
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiError(
      response.status === 401 || response.status === 403
        ? 'Your secure administrator session has expired.'
        : 'The secure API returned an unexpected response.',
      response.status,
      response.status === 401 || response.status === 403 ? 'session_expired' : 'unexpected_response'
    );
  }
  const envelope = (await response.json()) as ApiEnvelope<T>;
  if (!response.ok || !envelope.ok) {
    throw new ApiError(
      envelope.ok ? `HTTP ${response.status}` : envelope.error.message,
      response.status,
      envelope.ok ? `http_${response.status}` : envelope.error.code
    );
  }
  return envelope.data;
}
